import { useState } from "react";
import { api, errorText, useFetch } from "../api";
import { getAdmin } from "../auth";
import { Alerts, Badge, EmptyRow, Field, Loading, Modal, Page } from "../components/ui";
import { when } from "../format";

export default function Staff() {
  const admin = getAdmin() || {};
  const list = useFetch("/staff");
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function create(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setErrors({});
    setNotice("");
    try {
      await api("/staff", {
        method: "POST",
        body: { name: form.name.trim(), email: form.email.trim(), password: form.password },
      });
      setForm({ name: "", email: "", password: "" });
      setNotice("Staff account created");
      list.reload();
    } catch (err) {
      setError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title="Staff" text="New accounts are staff and active. You cannot edit your own row here, and a super admin cannot be deactivated here.">
      <Alerts error={list.error || error} notice={notice} />
      <form className="panel" onSubmit={create}>
        <h2 className="section-title">New staff</h2>
        <div className="form-grid">
          <Field label="Name" error={errors.name}>
            <input maxLength={60} value={form.name} onChange={(event) => set("name", event.target.value)} />
          </Field>
          <Field label="Email" error={errors.email}>
            <input type="email" value={form.email} onChange={(event) => set("email", event.target.value)} />
          </Field>
          <Field label="Password" error={errors.password} hint="At least 8 characters, with one letter and one number">
            <input type="password" value={form.password} onChange={(event) => set("password", event.target.value)} />
          </Field>
        </div>
        <div className="form-actions">
          <button className="btn" disabled={busy}>{busy ? "Saving..." : "Create staff"}</button>
        </div>
      </form>
      <h2 className="section-title">Accounts</h2>
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => {
                const mine = String(row.id) === String(admin.id);
                return (
                  <tr key={row.id}>
                    <td>{row.name}{mine ? " (you)" : ""}</td>
                    <td>{row.email}</td>
                    <td><Badge value={row.role} /></td>
                    <td><Badge value={row.status} /></td>
                    <td>{when(row.createdAt)}</td>
                    <td>
                      {mine ? "—" : (
                        <button type="button" className="btn ghost small" onClick={() => setEditor(row)}>Edit</button>
                      )}
                    </td>
                  </tr>
                );
              }) : <EmptyRow cols={6} text="No staff" />}
            </tbody>
          </table>
        </div>
      )}
      {editor ? (
        <StaffForm
          key={editor.id}
          row={editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            setNotice("Staff saved");
            list.reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function StaffForm({ row, onClose, onSaved }) {
  const [name, setName] = useState(row.name || "");
  const [status, setStatus] = useState(row.status || "active");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const locked = row.role === "super_admin";

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setErrors({});
    const body = { name: name.trim() };
    if (!locked) body.status = status;
    try {
      await api("/staff/" + row.id, { method: "PATCH", body });
      onSaved();
    } catch (err) {
      setError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Edit staff" onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Alerts error={error} />
        <div className="stack">
          <Field label="Name" error={errors.name}>
            <input maxLength={60} value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          {locked ? (
            <p className="hint">A super admin cannot be deactivated here.</p>
          ) : (
            <Field label="Status" error={errors.status}>
              <select value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          )}
        </div>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}
